import { IJsonSchema, OpenAPIV3_1 as OpenApi } from 'openapi-types';
import { ExternalApiEndpoint } from '~/queries';
import { ApiHelperContextType } from '../context';

type ComponentSchemas = Exclude<OpenApi.ComponentsObject['schemas'], undefined>;

/**
 * Convert a string from snake_case to Titled Case
 */
const fromSnakeCase = (input?: string) => {
  return (
    input
      ?.split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ') || ''
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const formatInputPayload = (input: any) => JSON.stringify(input, null, 2);
const DEFAULT_PAYLOAD = { any: 'valid json' };
const DUMMY_DATE_TIME = new Date().toISOString();

const getIsRefAnyValidJson = (ref?: string) =>
  ref === '#/components/schemas/anyValidJson' ||
  ref === '#/components/schemas/anyValidJsonOptional';

export const getRef = (
  input: string,
  componentSchemas: ComponentSchemas,
): { name: string; value: IJsonSchema } => {
  const baseRefPath = input.replace('#/components/schemas/', '');

  const refChunks = baseRefPath.split('/');
  const refName = refChunks[refChunks.length - 1];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let refValue = refChunks.reduce<Record<string, any>>(
    (acc, chunk) => (chunk in acc ? acc[chunk] : acc),
    componentSchemas,
  ) as IJsonSchema;

  if ('oneOf' in refValue && refValue.oneOf) {
    [refValue] = refValue.oneOf;
  }

  return {
    name: refName,
    value: refValue,
  };
};

function getPayloadInputElement(schema: IJsonSchema, componentSchemas: ComponentSchemas): unknown {
  if (schema.enum) return schema.enum[0];
  if (schema.type === 'object' && !schema.properties) return {};
  if (schema.type === 'object' && schema.properties) {
    return getPayloadInputElements(schema, componentSchemas);
  }

  if (schema.type === 'array' && schema.items && !Array.isArray(schema.items))
    return [getPayloadInputElement(schema.items, componentSchemas)];

  if (schema.type === 'boolean') return false;
  if (schema.type === 'number') return 1;

  if (schema.type === 'string' && 'format' in schema && schema.format === 'date-time')
    return DUMMY_DATE_TIME;

  return '';
}

function getPayloadInputElements(
  schema: IJsonSchema,
  componentSchemas: ComponentSchemas,
  root = false,
): unknown {
  const isRefAnyValidJson = '$ref' in schema && getIsRefAnyValidJson(schema.$ref);
  if (isRefAnyValidJson) {
    return DEFAULT_PAYLOAD;
  }

  const { properties, oneOf, ...schemaEntry } =
    '$ref' in schema && schema.$ref ? getRef(schema.$ref, componentSchemas).value : schema;

  if ('$ref' in schemaEntry && schemaEntry.$ref)
    return getPayloadInputElements(schemaEntry, componentSchemas);

  if (oneOf && root) {
    const inputList: unknown[] = [];
    oneOf.forEach((item) => {
      const { properties: itemProperties } =
        '$ref' in item && item.$ref ? getRef(item.$ref, componentSchemas).value : item;

      inputList.push({
        name: fromSnakeCase(itemProperties?.type?.enum?.[0] || 'Default'),
        payload: getPayloadInputElements(item, componentSchemas),
      });
    });
    return inputList;
  }

  if (oneOf) {
    return getPayloadInputElements(oneOf[0], componentSchemas);
  }

  if (properties) {
    const requiredFields = Object.fromEntries(
      (schemaEntry.required ?? []).map((item) => [item, true]),
    );

    const input: Record<string, unknown> = {};
    Object.entries(properties).forEach(([key, element]) => {
      if (requiredFields[key]) {
        input[key] = getPayloadInputElements(element, componentSchemas);
      }
    });
    return input;
  }

  return getPayloadInputElement(schema, componentSchemas);
}

export const getPayloadInputs = (
  endpoint: ExternalApiEndpoint,
  componentSchemas: ComponentSchemas,
): ApiHelperContextType['payloads'] => {
  const body = endpoint.requestBody as OpenApi.RequestBodyObject;
  if (!body) return { list: [], required: false };

  const schema = body?.content['application/json']?.schema as IJsonSchema;
  const payload = schema
    ? getPayloadInputElements(schema, componentSchemas, true)
    : DEFAULT_PAYLOAD;

  const payloadList = Array.isArray(payload) ? payload : [{ name: 'Default', payload }];
  return {
    list: payloadList.map((item) => ({ ...item, payload: formatInputPayload(item.payload) })),
    required: body.required ?? false,
  };
};
